/**
 * useResourceGroups
 * =================
 * React Query replacement for the `fetchResourceGroups` async thunk and the
 * `resourceGroupAdapter` entity adapter in commonSlice.
 *
 * The hook fetches resource groups for a given subscription via the shared
 * `resourcesProvider` (OneService CQRS) and normalises the result into the
 * same `ResourceGroup` shape the old Redux code produced.
 *
 * When `subscriptionId` is `undefined` or empty the query is disabled —
 * callers don't need their own guard.
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~services/queryKeys';
import { handleQueryError } from '~services/queryUtils';
import { resourcesProvider } from '~services/providers';
import type { ResourceGroup } from '~types/commonTypes';

async function fetchResourceGroups(
  subscriptionId: string
): Promise<ResourceGroup[]> {
  const entries = await resourcesProvider.listResourceGroups(subscriptionId);
  // Attach the subscriptionId to each entry, matching the shape the old
  // reducer produced.
  return (entries as unknown as ResourceGroup[]).map(rg => ({
    ...rg,
    subscriptionId,
  }));
}

export function useResourceGroups(subscriptionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.resourceGroups.list(subscriptionId),
    queryFn: () => fetchResourceGroups(subscriptionId!),
    enabled: !!subscriptionId,
    meta: { onError: handleQueryError },
  });
}

/**
 * Fetches resource groups for ALL given subscriptions and merges them into a
 * single flat array.  Useful for the admin sidebar / tree where we need
 * resource groups across every subscription.
 */
export function useAllResourceGroups(subscriptionIds: string[] | undefined) {
  return useQuery({
    queryKey: queryKeys.resourceGroups.list(subscriptionIds),
    queryFn: async () => {
      if (!subscriptionIds?.length) return [];
      const results = await Promise.allSettled(
        subscriptionIds.map(id => fetchResourceGroups(id))
      );
      return results.flatMap(r => (r.status === 'fulfilled' ? r.value : []));
    },
    enabled: !!subscriptionIds?.length,
    meta: { onError: handleQueryError },
  });
}
